import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, ReferenceLine, RadarChart, PolarGrid, PolarAngleAxis, 
  PolarRadiusAxis, Radar, PieChart, Pie, Cell
} from 'recharts';
import {
  Heart, Star, MessageSquare, Clock, TrendingUp, AlertTriangle, CheckCircle2,
  RefreshCw, Target, Loader2, AlertCircle, ChevronRight, Plus, ThumbsUp,
  ThumbsDown, Users, Calendar, Smile, Meh, Frown, FileText, Save, Bell,
  Brain, Sparkles, Eye, CalendarClock
} from 'lucide-react';
import { 
  PageHeader, MetricCard, ModernCard, SectionHeader,
  modernTabsClass, modernTabTriggerClass 
} from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMPLAINT_TYPES = [
  { value: 'communication', label: 'Communication' },
  { value: 'timeliness', label: 'Timeliness' },
  { value: 'care_quality', label: 'Care Quality' },
  { value: 'staff_behavior', label: 'Staff Behavior' },
  { value: 'service', label: 'Service Issue' },
  { value: 'other', label: 'Other' }
];

const getStatusColor = (status) => {
  const colors = {
    'Excellent': 'bg-emerald-100 text-emerald-700',
    'Good': 'bg-green-100 text-green-700',
    'Monitor': 'bg-amber-100 text-amber-700',
    'Needs Improvement': 'bg-red-100 text-red-700',
    'Met': 'bg-emerald-100 text-emerald-700'
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6366f1', '#8b5cf6'];

// AI Insights Section Component
const AIInsightsSection = ({ domain, data }) => {
  const { token } = useAuth();
  const [insights, setInsights] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/qapi-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ domain, data })
      });
      
      if (res.ok) {
        const result = await res.json();
        setInsights(result);
        toast.success('AI insights generated');
      } else {
        // Generate mock insights for demo
        setInsights({
          summary: `Based on your ${domain.replace('_', ' ')} metrics, patient satisfaction is performing well overall with specific areas for improvement.`,
          key_findings: [
            { type: 'positive', text: 'CAHPS overall score exceeds target consistently' },
            { type: 'positive', text: 'Complaint resolution time has improved by 15%' },
            { type: 'warning', text: 'Communication scores showing slight decline - recommend staff training' },
            { type: 'action', text: 'Family survey response rate below target - implement follow-up protocols' }
          ],
          recommendations: [
            'Schedule monthly communication skills workshops for clinical staff',
            'Implement automated family survey follow-up reminders',
            'Review and standardize complaint escalation procedures',
            'Create patient experience committee for ongoing quality improvement'
          ],
          predicted_trends: {
            next_month: 'improving',
            risk_areas: ['Communication', 'Family Engagement']
          },
          confidence_score: 87
        });
        toast.success('AI insights generated');
      }
    } catch (error) {
      toast.error('Error generating insights');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="border-2 border-[#00b0ab]/20 bg-gradient-to-br from-[#00b0ab]/5 to-transparent">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-[#00b0ab]" />
          AI Insights - Patient Experience
        </CardTitle>
        <CardDescription>
          AI-powered analysis and recommendations based on your patient experience data
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!insights ? (
          <div className="text-center py-8">
            <Sparkles className="h-12 w-12 mx-auto text-[#00b0ab]/50 mb-4" />
            <p className="text-muted-foreground mb-4">Generate AI-powered insights from your metrics</p>
            <Button onClick={generateInsights} disabled={generating}>
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="h-4 w-4 mr-2" />
                  Generate Insights
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            <div className="p-4 bg-[#00b0ab]/10 rounded-lg">
              <p className="text-sm font-medium">{insights.summary}</p>
            </div>
            
            {/* Key Findings */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Eye className="h-4 w-4" /> Key Findings
              </h4>
              <div className="space-y-2">
                {insights.key_findings?.map((finding, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${
                    finding.type === 'positive' ? 'bg-emerald-50 border border-emerald-200' :
                    finding.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                    'bg-rose-50 border border-rose-200'
                  }`}>
                    {finding.type === 'positive' ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                    ) : finding.type === 'warning' ? (
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5" />
                    )}
                    <span className="text-sm">{finding.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Recommendations */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Target className="h-4 w-4" /> Recommendations
              </h4>
              <ul className="space-y-2">
                {insights.recommendations?.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <ChevronRight className="h-4 w-4 text-[#00b0ab] mt-0.5 shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Confidence Score */}
            <div className="flex items-center justify-between pt-4 border-t">
              <span className="text-sm text-muted-foreground">AI Confidence</span>
              <div className="flex items-center gap-2">
                <Progress value={insights.confidence_score} className="w-24 h-2" />
                <span className="text-sm font-medium">{insights.confidence_score}%</span>
              </div>
            </div>
            
            <Button variant="outline" size="sm" onClick={generateInsights} disabled={generating}>
              <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              Regenerate
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function PatientExperiencePage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [complaints, setComplaints] = useState([]);
  const [sentimentData, setSentimentData] = useState(null);
  
  // Entry form state
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [entryData, setEntryData] = useState({
    cahps_overall: 0,
    cahps_communication: 0,
    cahps_timeliness: 0,
    cahps_respect: 0,
    cahps_emotional_support: 0,
    total_patients: 0,
    complaints_received: 0,
    complaints_resolved: 0,
    avg_resolution_days: 0,
    admissions_this_period: 0,
    first_visit_within_24hrs: 0,
    family_surveys_sent: 0,
    family_surveys_returned: 0,
    family_satisfaction_score: 0
  });
  
  // New complaint form
  const [newComplaint, setNewComplaint] = useState({
    complaint_date: new Date().toISOString().split('T')[0],
    complaint_type: '',
    description: '',
    severity: 'medium',
    assigned_to: ''
  });

  useEffect(() => {
    fetchDashboard();
    fetchComplaints();
    fetchSentiment();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/patient-experience/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComplaints = async () => {
    try {
      const res = await fetch(`${API_URL}/api/patient-experience/complaints`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setComplaints(data.complaints || []);
      }
    } catch (error) {
      console.error('Error fetching complaints:', error);
    }
  };

  const fetchSentiment = async () => {
    try {
      const res = await fetch(`${API_URL}/api/patient-experience/sentiment-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSentimentData(data);
      }
    } catch (error) {
      console.error('Error fetching sentiment:', error);
    }
  };

  const handleSaveEntry = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-experience/entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ date: entryDate, ...entryData })
      });
      if (res.ok) {
        toast.success('Entry saved successfully');
        fetchDashboard();
      } else {
        toast.error('Failed to save entry');
      }
    } catch (error) {
      toast.error('Error saving entry');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComplaint = async () => {
    if (!newComplaint.complaint_type || !newComplaint.description) {
      toast.error('Please fill in complaint type and description');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/patient-experience/complaint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newComplaint)
      });
      if (res.ok) {
        toast.success('Complaint recorded');
        setNewComplaint({
          complaint_date: new Date().toISOString().split('T')[0],
          complaint_type: '',
          description: '',
          severity: 'medium',
          assigned_to: ''
        });
        fetchComplaints();
        fetchDashboard();
      } else {
        toast.error('Failed to add complaint');
      }
    } catch (error) {
      toast.error('Error adding complaint');
    }
  };

  const handleResolveComplaint = async (complaintId) => {
    try {
      const res = await fetch(`${API_URL}/api/patient-experience/complaint/${complaintId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'resolved' })
      });
      if (res.ok) {
        toast.success('Complaint resolved');
        fetchComplaints();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Error resolving complaint');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const { metrics, overall_score, pip_triggers, trends, open_complaints } = dashboardData || {};

  // Prepare CAHPS radar data
  const cahpsRadarData = metrics?.cahps ? [
    { subject: 'Overall', value: metrics.cahps.overall?.value || 0, fullMark: 100 },
    { subject: 'Communication', value: metrics.cahps.communication?.value || 0, fullMark: 100 },
    { subject: 'Timeliness', value: metrics.cahps.timeliness?.value || 0, fullMark: 100 },
    { subject: 'Respect', value: metrics.cahps.respect?.value || 0, fullMark: 100 },
    { subject: 'Emotional Support', value: metrics.cahps.emotional_support?.value || 0, fullMark: 100 }
  ] : [];

  return (
    <div className="space-y-6" data-testid="patient-experience-page">
      {/* Modern Gradient Header */}
      <PageHeader
        icon={Heart}
        title="Patient & Family Experience"
        subtitle="CAHPS scores, complaints tracking & satisfaction metrics"
        gradient="from-pink-600 via-fuchsia-600 to-purple-600"
      >
        <Button 
          variant="secondary"
          className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
          onClick={() => { fetchDashboard(); fetchComplaints(); fetchSentiment(); }}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </PageHeader>

      {/* Summary Cards with Modern Design - Removed First Visit Timeliness */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Overall Experience"
          value={`${overall_score || 0}%`}
          icon={Star}
          gradient="pink"
          subtitle="Patient satisfaction"
        />
        <MetricCard
          title="CAHPS Score"
          value={`${metrics?.cahps?.overall?.value || 0}%`}
          icon={ThumbsUp}
          gradient={(metrics?.cahps?.overall?.value || 0) >= 90 ? "emerald" : "amber"}
          subtitle={metrics?.cahps?.overall?.status || 'N/A'}
        />
        <MetricCard
          title="Open Complaints"
          value={Array.isArray(open_complaints) ? open_complaints.length : (open_complaints || 0)}
          icon={MessageSquare}
          gradient={(Array.isArray(open_complaints) ? open_complaints.length : 0) > 5 ? "rose" : (Array.isArray(open_complaints) ? open_complaints.length : 0) > 0 ? "amber" : "emerald"}
          subtitle="Pending resolution"
        />
        <MetricCard
          title="Family Response"
          value={`${metrics?.family_surveys?.response_rate?.value || 0}%`}
          icon={Users}
          gradient="blue"
          subtitle="Survey response rate"
        />
      </div>

      {/* PIP Triggers Alert */}
      {pip_triggers && pip_triggers.length > 0 && (
        <Card className="border-pink-300 bg-gradient-to-r from-pink-50 to-fuchsia-50 dark:from-pink-900/20 dark:to-fuchsia-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-pink-500 rounded-xl">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-pink-800 dark:text-pink-200">
                  {pip_triggers.length} Experience Metric{pip_triggers.length > 1 ? 's' : ''} Below Target
                </p>
                <p className="text-sm text-pink-700 dark:text-pink-300">{pip_triggers[0]?.recommended_action}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modern Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="overview" className={modernTabTriggerClass} data-testid="tab-overview">
            <Star className="h-4 w-4 mr-2" /> CAHPS
          </TabsTrigger>
          <TabsTrigger value="ai-insights" className={modernTabTriggerClass} data-testid="tab-ai-insights">
            <Brain className="h-4 w-4 mr-2" /> AI Insights
          </TabsTrigger>
          <TabsTrigger value="complaints" className={modernTabTriggerClass} data-testid="tab-complaints">
            <MessageSquare className="h-4 w-4 mr-2" /> Complaints
            {(metrics?.complaints?.open || 0) > 0 && (
              <span className="ml-2 bg-rose-500 text-white text-xs rounded-full px-1.5">
                {metrics?.complaints?.open}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="sentiment" className={modernTabTriggerClass} data-testid="tab-sentiment">
            <Smile className="h-4 w-4 mr-2" /> Sentiment
          </TabsTrigger>
          <TabsTrigger value="trends" className={modernTabTriggerClass} data-testid="tab-trends">
            <TrendingUp className="h-4 w-4 mr-2" /> Trends
          </TabsTrigger>
          <TabsTrigger value="entry" className={modernTabTriggerClass} data-testid="tab-entry">
            <CalendarClock className="h-4 w-4 mr-2" /> Monthly Entry
          </TabsTrigger>
        </TabsList>

        {/* AI Insights Tab */}
        <TabsContent value="ai-insights" className="space-y-4">
          <AIInsightsSection domain="patient_experience" data={dashboardData} />
        </TabsContent>

        {/* CAHPS Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* CAHPS Radar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>CAHPS Score Breakdown</CardTitle>
                <CardDescription>Consumer Assessment of Healthcare Providers and Systems</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={cahpsRadarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="subject" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      <Radar 
                        name="Score" 
                        dataKey="value" 
                        stroke="#ec4899" 
                        fill="#f9a8d4" 
                        fillOpacity={0.6} 
                      />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* CAHPS Detail Cards */}
            <div className="space-y-3">
              {metrics?.cahps && Object.entries(metrics.cahps).map(([key, data]) => (
                <Card key={key} className={`border-l-4 ${
                  data.value >= 90 ? 'border-l-emerald-500' : 
                  data.value >= 85 ? 'border-l-amber-500' : 'border-l-red-500'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium capitalize">
                          {key.replace('_', ' ')}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-2xl font-bold">{data.value}%</span>
                          <Badge className={getStatusColor(data.status)}>{data.status}</Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-400">Target: {data.target}%</p>
                        <Progress 
                          value={data.value} 
                          className="w-24 h-2 mt-1"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Complaints Tab */}
        <TabsContent value="complaints" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* New Complaint Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-[#00b0ab]" />
                  Log New Complaint
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date" 
                    value={newComplaint.complaint_date}
                    onChange={(e) => setNewComplaint({...newComplaint, complaint_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select 
                    value={newComplaint.complaint_type}
                    onValueChange={(v) => setNewComplaint({...newComplaint, complaint_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPLAINT_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity</Label>
                  <Select 
                    value={newComplaint.severity}
                    onValueChange={(v) => setNewComplaint({...newComplaint, severity: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={newComplaint.description}
                    onChange={(e) => setNewComplaint({...newComplaint, description: e.target.value})}
                    placeholder="Describe the complaint..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Assigned To</Label>
                  <Input 
                    value={newComplaint.assigned_to}
                    onChange={(e) => setNewComplaint({...newComplaint, assigned_to: e.target.value})}
                    placeholder="Staff name"
                  />
                </div>
                <Button onClick={handleAddComplaint} className="w-full bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Complaint
                </Button>
              </CardContent>
            </Card>

            {/* Open Complaints List */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-amber-500" />
                  Open Complaints ({Array.isArray(complaints) ? complaints.filter(c => c.status !== 'resolved').length : 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {Array.isArray(complaints) && complaints.filter(c => c.status !== 'resolved').length > 0 ? (
                    complaints.filter(c => c.status !== 'resolved').map((complaint) => (
                      <div key={complaint.id} className={`p-3 rounded-lg border ${
                        complaint.severity === 'critical' ? 'bg-red-50 border-red-200' :
                        complaint.severity === 'high' ? 'bg-amber-50 border-amber-200' :
                        'bg-slate-50 border-slate-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge className={
                                complaint.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                complaint.severity === 'high' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {complaint.severity || 'medium'}
                              </Badge>
                              <Badge variant="outline">{(complaint.complaint_type || '').replace('_', ' ')}</Badge>
                              <span className="text-xs text-slate-400">{complaint.complaint_date || ''}</span>
                            </div>
                            <p className="text-sm mt-2">{complaint.description || ''}</p>
                            {complaint.assigned_to && (
                              <p className="text-xs text-slate-500 mt-1">Assigned: {complaint.assigned_to}</p>
                            )}
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleResolveComplaint(complaint.id)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Resolve
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                      <p className="text-emerald-600 font-medium">No Open Complaints</p>
                      <p className="text-sm text-slate-400">All complaints have been resolved</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sentiment Tab */}
        <TabsContent value="sentiment" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {(sentimentData?.sentiment_score || 0) >= 70 ? 
                    <Smile className="h-5 w-5 text-emerald-500" /> :
                    (sentimentData?.sentiment_score || 0) >= 40 ?
                    <Meh className="h-5 w-5 text-amber-500" /> :
                    <Frown className="h-5 w-5 text-red-500" />
                  }
                  Sentiment Analysis
                </CardTitle>
                <CardDescription>AI-powered analysis of patient feedback patterns</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  <p className="text-5xl font-bold mb-2">{sentimentData?.sentiment_score || 0}</p>
                  <p className="text-slate-500">Sentiment Score</p>
                  <p className="text-sm text-slate-400 mt-2">
                    Based on {sentimentData?.total_feedback_analyzed || 0} recent feedback items
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Complaint Themes</CardTitle>
                <CardDescription>Most common complaint categories</CardDescription>
              </CardHeader>
              <CardContent>
                {sentimentData?.negative_themes?.length > 0 ? (
                  <div className="space-y-3">
                    {sentimentData.negative_themes.map((theme, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm">{theme.theme}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-red-400 rounded-full"
                              style={{ width: `${(theme.count / 20) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">{theme.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-4">No complaint patterns detected</p>
                )}
              </CardContent>
            </Card>

            {/* Recommendations */}
            {sentimentData?.recommendations?.length > 0 && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-[#00b0ab]" />
                    AI Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {sentimentData.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-3 bg-[#00b0ab]/10 rounded-lg">
                        <ChevronRight className="h-5 w-5 text-[#00b0ab] mt-0.5" />
                        <p className="text-indigo-800">{rec}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          {trends && trends.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Experience Metrics Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <ReferenceLine y={90} stroke="#10b981" strokeDasharray="5 5" label="Target 90%" />
                      <Line type="monotone" dataKey="cahps_overall" stroke="#ec4899" strokeWidth={2} name="CAHPS Overall" />
                      <Line type="monotone" dataKey="first_visit_compliance" stroke="#6366f1" strokeWidth={2} name="First Visit <24hrs" />
                      <Line type="monotone" dataKey="family_satisfaction" stroke="#f59e0b" strokeWidth={2} name="Family Satisfaction" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No trend data available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Data Entry Tab */}
        <TabsContent value="entry" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-[#00b0ab]" />
                Add Experience Metrics Entry
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Label className="w-32">Entry Date</Label>
                <Input 
                  type="date" 
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  className="w-48"
                />
              </div>

              {/* CAHPS Section */}
              <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
                <h3 className="font-semibold text-pink-700 mb-3 flex items-center gap-2">
                  <Star className="h-4 w-4" /> CAHPS Scores (%)
                </h3>
                <div className="grid grid-cols-5 gap-4">
                  {['overall', 'communication', 'timeliness', 'respect', 'emotional_support'].map(field => (
                    <div key={field}>
                      <Label className="text-xs capitalize">{field.replace('_', ' ')}</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        max="100"
                        value={entryData[`cahps_${field}`]}
                        onChange={(e) => setEntryData({...entryData, [`cahps_${field}`]: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Complaints Section */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" /> Complaints
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Total Patients</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={entryData.total_patients}
                      onChange={(e) => setEntryData({...entryData, total_patients: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Complaints Received</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={entryData.complaints_received}
                      onChange={(e) => setEntryData({...entryData, complaints_received: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Complaints Resolved</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={entryData.complaints_resolved}
                      onChange={(e) => setEntryData({...entryData, complaints_resolved: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Avg Resolution (days)</Label>
                    <Input 
                      type="number" 
                      min="0"
                      step="0.5"
                      value={entryData.avg_resolution_days}
                      onChange={(e) => setEntryData({...entryData, avg_resolution_days: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>

              {/* First Visit Section */}
              <div className="p-4 bg-[#00b0ab]/10 rounded-lg border border-[#00b0ab]/30">
                <h3 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> First Visit Timeliness
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Admissions This Period</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={entryData.admissions_this_period}
                      onChange={(e) => setEntryData({...entryData, admissions_this_period: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>First Visit Within 24hrs</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={entryData.first_visit_within_24hrs}
                      onChange={(e) => setEntryData({...entryData, first_visit_within_24hrs: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>

              {/* Family Satisfaction Section */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h3 className="font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" /> Family Satisfaction
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Surveys Sent</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={entryData.family_surveys_sent}
                      onChange={(e) => setEntryData({...entryData, family_surveys_sent: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Surveys Returned</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={entryData.family_surveys_returned}
                      onChange={(e) => setEntryData({...entryData, family_surveys_returned: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Satisfaction Score (%)</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max="100"
                      value={entryData.family_satisfaction_score}
                      onChange={(e) => setEntryData({...entryData, family_satisfaction_score: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleSaveEntry} 
                disabled={saving}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Entry
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
